import { trimStart } from 'lodash';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getInternalFolders,
  getProviderFolders,
  getRemoteFolders,
} from '~/api/evisync';
import ProviderLogo from '~/components/Shared/ProviderLogo/ProviderLogo';
import { Button } from '~/eds/src/components/Button/Button';
import { Checkbox } from '~/eds/src/components/Checkbox/Checkbox';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useAsync } from '~/hooks/useAsync';
import { FormattedMessage, useIntl } from 'react-intl';
import { slices } from '~/redux/slices';
import Divider from '~/ui/components/Divider/Divider';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import FolderInput from '~/ui/components/FolderInput/FolderInput';
import InputLabel from '~/ui/components/InputLabel/InputLabel';
import Link from '~/ui/components/Link/Link';
import MultiSelect from '~/ui/components/MultiSelect/MultiSelect';
import Text from '~/ui/components/Text/Text';
import { debounce } from '~/utils/helper.utils';

import {
  DEFAULT_FILE_TYPES,
  PROCESSABLE_DOCUMENT_TYPES,
} from '../../constants';
import { testIsFileTypesEqual, testIsValidSyncPair } from '../../util';
import Footer from '../Footer';

function Folders() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  );

  const {
    allowedSyncFileTypes,
    evisortFolder,
    name,
    path,
    provider,
    providerFolder,
    tokenId,
  } = activeSyncPair;

  const enableSharepointSyncSiteSearch = useFlag(
    FlagType.SharepointSyncSiteSearch,
  );
  const enableSkipEmptyFolders = useFlag(FlagType.SkipEmptyFolders);

  const [providerFolders, setProviderFolders] = useState([]);
  const [search, setSearchTerm] = useState([]);

  const fetchProviderSubfolders = async (folder) => {
    return await getRemoteFolders({
      path: folder.path,
      provider,
      providerOptions: folder.providerOptions,
      providerPrefix: folder.providerPrefix,
      tokenId,
    });
  };

  const { isLoading: isLoadingProviderFolders } = useAsync(
    getProviderFolders,
    {
      path,
      provider,
      tokenId,
      search,
    },
    {
      condition: true,
      deps: [provider, search],
      successHandler: setProviderFolders,
      errorToastMessage: intl.formatMessage({
        id: 'Folders.providerFoldersError',
        defaultMessage:
          'There is an error fetching remote folders, please try refreshing the page.',
        description: 'Error message when fetching provider folders fails',
      }),
    },
  );

  const { isLoading: isLoadingInternalFolders, response: internalFolders } =
    useAsync(
      getInternalFolders,
      {},
      {
        condition: true,
        errorToastMessage: intl.formatMessage({
          id: 'Folders.internalFoldersError',
          defaultMessage:
            'There is an error fetching Evisort folders, please try refreshing the page.',
          description: 'Error message when fetching internal folders fails',
        }),
      },
    );

  const fileTypeOptions = allowedSyncFileTypes.map((fileExtensionType) => {
    const option = {
      value: fileExtensionType,
      label: trimStart(fileExtensionType, '.'),
    };
    if (DEFAULT_FILE_TYPES.includes(fileExtensionType)) {
      option.isDefault = true;
    }
    return option;
  });

  const handleNext = () => {
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.Summary,
      ),
    );
  };

  const handleBack = () => {
    // remove tokenId, state, code to get a new auth token when going back
    const {
      tokenId: _tokenId,
      state: _state,
      code: _code,
      ...activeSyncPairData
    } = activeSyncPair;
    dispatch(slices.integrations.actions.setActiveSyncPair(activeSyncPairData));
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.Authentication,
      ),
    );
  };

  function handleSkipEmptyFolders() {
    const skipEmptyFolders = !activeSyncPair?.skipEmptyFolders;
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        skipEmptyFolders,
      }),
    );
  }

  const setAllowedSyncFileTypes = (types) => {
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        allowedSyncFileTypes: types.map((type) => '.' + trimStart(type, '.')),
      }),
    );
  };

  const onSelectInternalFolder = (folder) => {
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        folderId: folder.folderId,
        evisortFolder: folder,
      }),
    );
  };

  const onSelectProviderFolder = (folder) => {
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        providerFolder: folder,
        providerPrefix: folder.providerPrefix,
        providerFolderPath: folder.path,
        providerOptions: {
          ...folder.providerOptions,
          _folder_id: folder.id,
        },
      }),
    );
  };

  const isFileTypesEqual = testIsFileTypesEqual(allowedSyncFileTypes);

  const errors = {};

  const emptyProviderFolderModalContent = (
    <FlexLayout alignItems="center" flexDirection="column">
      <Text color="gray-600" variant="xs-dense">
        <FormattedMessage
          id="Folders.emptyFoldersMessage"
          defaultMessage="You do not have any folders in your account."
          description="Message when user has no folders"
        />
      </Text>
      <Text color="gray-600" variant="xs-dense">
        <FormattedMessage
          id="Folders.reauthenticateMessage"
          defaultMessage="Please <link>re-try and reauthenticate</link> your account"
          description="Message prompting user to reauthenticate"
          values={{
            link: (chunks) => (
              <Link variant="xs-dense" onClick={handleBack}>
                {chunks}
              </Link>
            ),
          }}
        />
      </Text>
    </FlexLayout>
  );

  if (allowedSyncFileTypes.length === 0) {
    errors.allowedSyncFileTypes = intl.formatMessage({
      id: 'Folders.fileTypesError',
      defaultMessage:
        'You have not added any file types. Please include at least one file type',
      description: 'Error message when no file types are selected',
    });
  }

  const searchUpdate = debounce((text) => {
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        folderId: null,
      }),
    );
    setSearchTerm(text);
  }, 500);

  const sharepointSearchDescription = intl.formatMessage({
    id: 'Folders.sharepointSearchDescription',
    defaultMessage:
      'If the Sharepoint site you are looking for cannot be found in the folder list above, enter a search term to narrow down the list of sites.',
    description: 'Description for SharePoint site search functionality',
  });

  return (
    <FlexLayout alignItems="stretch" flexDirection="column" space={8}>
      <Layout direction="row" justify="center" spacing={6}>
        <ProviderLogo provider={provider} />
        <Layout direction="column" justify="center">
          <Icon icon={'arrow-right'} size="l" color="status.info" />
          <Icon icon={'arrow-left'} size="l" color="status.info" />
        </Layout>
        <ProviderLogo provider="evisort" />
      </Layout>
      <InputLabel isRequired label={`${name} folder`}>
        <FolderInput
          folder={providerFolder}
          folderTreeProps={{
            emptyContentMessage: emptyProviderFolderModalContent,
            folders: providerFolders,
            label: `${name} Folder Tree`,
            asyncFetchSubfolders: fetchProviderSubfolders,
          }}
          id={OnboardingIdType.SelectProviderFolder}
          isLoading={isLoadingProviderFolders}
          modalTitle={intl.formatMessage(
            {
              id: 'Folders.selectProviderFolderTitle',
              defaultMessage: 'Select a {name} folder',
              description:
                'Modal title for selecting provider folder. Example: name = "Document Name"',
            },
            { name },
          )}
          onChange={onSelectProviderFolder}
        />
        {enableSkipEmptyFolders && (
          <FlexLayout flexDirection="row">
            <Checkbox
              option={{
                value: 'skip-empty-folders',
                label: intl.formatMessage({
                  id: 'Folders.skipEmptyFoldersLabel',
                  defaultMessage: 'Skip empty folders',
                  description: 'Label for skip empty folders checkbox',
                }),
              }}
              value={!!activeSyncPair?.skipEmptyFolders}
              onChange={handleSkipEmptyFolders}
            />
            <Tooltip
              tooltip={intl.formatMessage({
                id: 'Folders.skipEmptyFoldersTooltip',
                defaultMessage:
                  'Skipping empty folders may speed up initial synchronization time.',
                description: 'Tooltip explaining skip empty folders feature',
              })}
              placement="bottom"
            >
              <Icon icon="info" />
            </Tooltip>
          </FlexLayout>
        )}
      </InputLabel>
      {provider === 'SHARE_POINT' && enableSharepointSyncSiteSearch && (
        <InputLabel
          hidden={provider !== 'SHARE_POINT'}
          label={intl.formatMessage({
            id: 'Folders.sharepointSearchLabel',
            defaultMessage: 'Sharepoint site search term',
            description: 'Label for SharePoint site search input',
          })}
          description={sharepointSearchDescription}
        >
          <TextInput
            id="sharepoint-site-search"
            placeholder={intl.formatMessage({
              id: 'Folders.sharepointSearchPlaceholder',
              defaultMessage: 'Enter a sharepoint site search term',
              description: 'Placeholder for SharePoint site search input',
            })}
            onChange={searchUpdate}
            width="fullWidth"
          />
        </InputLabel>
      )}
      <InputLabel
        isRequired
        label={intl.formatMessage({
          id: 'Folders.evisortFolderLabel',
          defaultMessage: 'Evisort folder',
          description: 'Label for Evisort folder selection',
        })}
      >
        <FolderInput
          folderTreeProps={{
            folders: internalFolders,
            label: 'Evisort Folder Tree',
          }}
          folder={evisortFolder}
          id={OnboardingIdType.SelectEvisortFolder}
          isLoading={isLoadingInternalFolders}
          modalTitle={intl.formatMessage({
            id: 'Folders.selectEvisortFolderTitle',
            defaultMessage: 'Select an Evisort folder',
            description: 'Modal title for selecting Evisort folder',
          })}
          onChange={onSelectInternalFolder}
        />
      </InputLabel>
      <Divider />
      <FlexLayout
        alignItems="flex-end"
        id={OnboardingIdType.SelectFileTypes}
        space={8}
      >
        <InputLabel
          isRequired
          description={
            <FormattedMessage
              id="Folders.fileTypesDescription"
              defaultMessage="Selected file types will show in your Evisort folders, but only <strong>{processableTypes}</strong> will be processed by Evisort's AI."
              description="Description explaining file type processing. Example: processableTypes = '.pdf, .docx, .doc'"
              values={{
                strong: (chunks) => <strong>{chunks}</strong>,
                processableTypes: PROCESSABLE_DOCUMENT_TYPES.join(', '),
              }}
            />
          }
          label={intl.formatMessage({
            id: 'Folders.fileTypesLabel',
            defaultMessage: 'Synchronize the following file types',
            description: 'Label for file types selection',
          })}
        >
          <MultiSelect
            error={errors.allowedSyncFileTypes}
            noOptionsMessage={intl.formatMessage({
              id: 'Folders.fileTypesNoOptions',
              defaultMessage:
                'Enter an extension for a file type you want to add',
              description: 'Message when no file type options available',
            })}
            options={fileTypeOptions}
            placeholder={intl.formatMessage({
              id: 'Folders.fileTypesPlaceholder',
              defaultMessage: 'Add a file extension',
              description: 'Placeholder for file types input',
            })}
            values={allowedSyncFileTypes}
            width="fullWidth"
            formatCreateLabel={(label) =>
              intl.formatMessage(
                {
                  id: 'Folders.addCustomFileType',
                  defaultMessage: 'Add custom .{label} file type',
                  description:
                    'Label for adding custom file type. Example: label = "Important"',
                },
                { label },
              )
            }
            onAddOption={(x) => x}
            onChange={setAllowedSyncFileTypes}
          />
        </InputLabel>
        <FlexLayout flex="none">
          <Button
            tooltip={isFileTypesEqual ? null : DEFAULT_FILE_TYPES.join(', ')}
            disabled={isFileTypesEqual}
            text={intl.formatMessage({
              id: 'Folders.resetToDefaults',
              defaultMessage: 'Reset to defaults',
              description: 'Button text for resetting file types to defaults',
            })}
            variant="secondary"
            onClick={() => setAllowedSyncFileTypes(DEFAULT_FILE_TYPES)}
          />
        </FlexLayout>
      </FlexLayout>
      <Footer
        leftAction={{
          text: intl.formatMessage({
            id: 'Folders.reauthenticateButton',
            defaultMessage: 'Reauthenticate',
            description: 'Button text for reauthentication',
          }),
          onClick: handleBack,
        }}
        rightAction={{
          disabled: !testIsValidSyncPair(activeSyncPair),
          iconRight: 'arrowRight',
          text: intl.formatMessage({
            id: 'Folders.nextButton',
            defaultMessage: 'Next',
            description: 'Button text for proceeding to next step',
          }),
          onClick: handleNext,
        }}
      />
    </FlexLayout>
  );
}

export default Folders;
